import { Flex } from "@chakra-ui/react-v3";
import { FhirBundleSdk } from "@metriport/fhir-sdk";
import dayjs from "dayjs";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import { useMemo, useState } from "react";

dayjs.extend(isSameOrAfter);
import { BaseTableLayout, ColumnDef } from "../../components/base-table-layout";
import { LabelFilterSegmentedControl } from "../../components/label-filter-segmented-control";
import { TableFilterPopover } from "../../components/table-filter-popover";
import { useTableFilters } from "../../hooks/use-table-filters";
import { TableRow } from "../../utils/tables-metadata";
import { CareTeamRowData } from "./care-team-rows";

type TimeFilter = "all" | "6months" | "1year";

export type CareTeamTableProps = {
  rows: TableRow[];
  onRowClick: (row: TableRow) => void;
  fhirSdk: FhirBundleSdk;
  filterQuery: string;
  onPdfView?: (docId: string) => void;
  displayName?: string;
  isRowViewed?: (row: TableRow) => boolean;
};

export function CareTeamTable({
  rows,
  onRowClick,
  filterQuery,
  displayName = "Care Team",
  isRowViewed,
}: CareTeamTableProps) {
  const [timeFilter, setTimeFilter] = useState<TimeFilter>("all");

  const columns: ColumnDef<CareTeamRowData>[] = useMemo(
    () => [
      { key: "name", header: "Name", maxWidth: "250px" },
      { key: "specialty", header: "Specialty", maxWidth: "200px" },
      { key: "phone", header: "Phone", maxWidth: "180px" },
      { key: "email", header: "Email", maxWidth: "220px" },
      { key: "address", header: "Address", maxWidth: "300px" },
      { key: "mostRecentAppointment", header: "Last Appointment", sort: "desc", maxWidth: "150px" },
    ],
    []
  );

  // Create specialty filter options dynamically from data
  const specialtyFilterOptions = useMemo(() => {
    const careTeamRows = rows as CareTeamRowData[];
    const specialties = new Set<string>();

    careTeamRows.forEach(row => {
      if (row.specialty && row.specialty !== "-") {
        specialties.add(row.specialty);
      }
    });

    const sortedSpecialties = Array.from(specialties).sort();

    return sortedSpecialties.map(specialty => ({
      label: specialty,
      groupLabel: "Specialty",
      isChecked: true,
      filterFn: (rowData: CareTeamRowData) => rowData.specialty === specialty,
    }));
  }, [rows]);

  const {
    activeFilters,
    setActiveFilters,
    filteredRows: specialtyFilteredRows,
  } = useTableFilters(rows, specialtyFilterOptions);

  // Apply time filter on top of specialty filter
  const timeFilteredRows = useMemo(() => {
    if (timeFilter === "all") {
      return specialtyFilteredRows;
    }

    const now = dayjs();
    const cutoffDate =
      timeFilter === "6months" ? now.subtract(6, "month") : now.subtract(1, "year");

    return specialtyFilteredRows.filter(row => {
      const careTeamRow = row as CareTeamRowData;
      if (!careTeamRow.mostRecentAppointmentDate) {
        return false;
      }
      return dayjs(careTeamRow.mostRecentAppointmentDate).isSameOrAfter(cutoffDate, "day");
    });
  }, [specialtyFilteredRows, timeFilter]);

  function handleRowClick(row: TableRow) {
    onRowClick(row);
  }

  return (
    <BaseTableLayout
      title={displayName}
      rows={timeFilteredRows as unknown as CareTeamRowData[]}
      columns={columns}
      filterQuery={filterQuery}
      onRowClick={handleRowClick}
      isRowViewed={isRowViewed}
      headerActionsSlot={
        <Flex gap={2}>
          <LabelFilterSegmentedControl
            value={timeFilter}
            onValueChange={setTimeFilter}
            items={[
              { value: "all", label: "All" },
              { value: "6months", label: "Last 6 Months" },
              { value: "1year", label: "Last Year" },
            ]}
          />
          {specialtyFilterOptions.length > 0 && (
            <TableFilterPopover
              filters={specialtyFilterOptions}
              activeFilters={activeFilters}
              setActiveFilters={newFilters => setActiveFilters(newFilters)}
            />
          )}
        </Flex>
      }
    />
  );
}
